import React, { useState, useEffect } from 'react';
import {
  Search, Loader2, AlertCircle, Users, ArrowUpRight,
  ChevronLeft, ChevronRight, Download,
} from 'lucide-react';
import { supabase } from '@/utils/supabase';

function fmtCurrency(cents) {
  return '$' + (cents / 100).toFixed(2);
}

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [search, setSearch]       = useState('');
  const [page, setPage]           = useState(0);
  const PAGE_SIZE = 20;

  useEffect(() => { loadCustomers(); }, [search]);

  const loadCustomers = async () => {
    setLoading(true);
    setError('');
    try {
      // Aggregate customers from orders
      let query = supabase
        .from('orders')
        .select('customer_email, customer_name, customer_phone, amount_total, created_at')
        .eq('status', 'paid')
        .not('customer_email', 'is', null)
        .order('created_at', { ascending: false });

      if (search) {
        query = query.or(`customer_email.ilike.%${search}%,customer_name.ilike.%${search}%`);
      }

      const { data, error: qErr } = await query;
      if (qErr) throw qErr;

      // Group by email
      const map = new Map();
      for (const row of (data || [])) {
        const email = row.customer_email;
        if (!map.has(email)) {
          map.set(email, {
            email,
            name: row.customer_name,
            phone: row.customer_phone,
            orders: 0,
            ltv: 0,
            firstOrder: row.created_at,
            lastOrder: row.created_at,
          });
        }
        const c = map.get(email);
        c.orders++;
        c.ltv += row.amount_total || 0;
        if (row.created_at < c.firstOrder) c.firstOrder = row.created_at;
        if (row.created_at > c.lastOrder)  c.lastOrder  = row.created_at;
      }

      setCustomers([...map.values()].sort((a, b) => b.ltv - a.ltv));
      setPage(0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    const header = 'Email,Name,Phone,Orders,Lifetime Value,First Order,Last Order\n';
    const rows = customers.map((c) =>
      [
        c.email,
        `"${c.name || ''}"`,
        c.phone || '',
        c.orders,
        (c.ltv / 100).toFixed(2),
        c.firstOrder ? new Date(c.firstOrder).toLocaleDateString() : '',
        c.lastOrder  ? new Date(c.lastOrder).toLocaleDateString()  : '',
      ].join(',')
    ).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'customers.csv';
    a.click();
  };

  const paged = customers.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(customers.length / PAGE_SIZE);

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl text-[var(--ink)]">Customers</h1>
          <p className="text-[var(--muted)] text-xs font-['Space_Mono'] mt-0.5">
            {customers.length} unique customer{customers.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={exportCSV}
          className="flex items-center gap-2 border border-[var(--line)] text-[var(--muted)] hover:text-[var(--ink)] px-3 py-2 text-[0.62rem] font-['Space_Mono'] tracking-[0.1em] uppercase transition-colors"
        >
          <Download size={11} /> Export CSV
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email…"
          className="w-full field-surface pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors"
        />
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 text-red-400 text-xs font-['Space_Mono'] p-3 bg-red-400/10 border border-red-400/20">
          <AlertCircle size={12} /><span>{error}</span>
        </div>
      )}

      {/* Table */}
      <div className="bg-[var(--paper)] border border-[var(--line)] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 size={18} className="animate-spin text-[var(--brand)]" />
          </div>
        ) : paged.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 gap-3">
            <Users size={28} className="text-[var(--muted)]" strokeWidth={1} />
            <p className="text-[var(--muted)] text-sm font-['Space_Grotesk']">No customers found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[var(--line)]">
                  {['Customer', 'Orders', 'Lifetime Value', 'Last Order', 'First Order'].map((h) => (
                    <th key={h} className="text-left px-5 py-3 font-['Space_Mono'] text-[0.58rem] tracking-[0.12em] uppercase text-[var(--muted)] whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--line)]">
                {paged.map((c) => (
                  <tr key={c.email} className="hover:bg-white/3 transition-colors">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-full bg-[var(--brand)]/20 border border-[var(--brand)]/30 flex items-center justify-center shrink-0">
                          <span className="font-['Space_Mono'] text-[0.6rem] text-[var(--brand)] uppercase">
                            {(c.name || c.email || '?')[0]}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-[var(--ink)]">{c.name || '—'}</p>
                          <p className="text-[var(--muted)] text-[0.6rem] font-['Space_Mono'] truncate">{c.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 font-['Space_Mono'] text-[var(--ink)]">{c.orders}</td>
                    <td className="px-5 py-3 font-['Space_Mono'] text-[var(--brand)]">
                      {fmtCurrency(c.ltv)}
                    </td>
                    <td className="px-5 py-3 text-[var(--muted)] whitespace-nowrap">
                      {c.lastOrder ? new Date(c.lastOrder).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-5 py-3 text-[var(--muted)] whitespace-nowrap">
                      {c.firstOrder ? new Date(c.firstOrder).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-[var(--muted)] text-xs font-['Space_Mono']">
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, customers.length)} of {customers.length}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="p-1.5 border border-[var(--line)] text-[var(--muted)] hover:text-[var(--ink)] disabled:opacity-40 transition-colors"
            >
              <ChevronLeft size={13} />
            </button>
            <span className="px-3 text-xs font-['Space_Mono'] text-[var(--muted)]">
              {page + 1} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="p-1.5 border border-[var(--line)] text-[var(--muted)] hover:text-[var(--ink)] disabled:opacity-40 transition-colors"
            >
              <ChevronRight size={13} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
